import React, { useState, useEffect, ChangeEvent, FormEvent, useMemo } from 'react';
import api from '../../api/axiosConfig';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import imageCompression from "browser-image-compression"; // ✅ Import nén ảnh

// Helper function to create absolute URL for assets
const createAbsoluteUrl = (path: string | undefined | null) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) { return path; }
  const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
  return `${base}${path}`;
};

// =============================================================================
// 1. CẬP NHẬT INTERFACE & BLANK ARTICLE
// =============================================================================
interface NewsData {
  _id?: string;
  slug: string;
  isVisible: boolean;
  
  // ✅ Thay đổi cấu trúc thumbnail
  thumbnail: {
    url: string;
    public_id: string;
  };
  oldThumbnailId?: string; // ✅ Field để gửi BE xoá ảnh cũ

  title_vi: string;
  title_en: string;
  content_vi: string;
  content_en: string;
  author_vi: string;
  author_en: string;
  category_vi: string;
  category_en: string;
  createdAt?: string;
}

const getAuthToken = (): string | null => {
  const userString = sessionStorage.getItem('user');
  if (userString) {
    try {
      const userData = JSON.parse(userString);
      return userData.accessToken || null;
    } catch (error) { 
      return null;
    }
  }
  return null;
};

const blankArticle: NewsData = {
  slug: '', isVisible: false, 
  thumbnail: { url: '', public_id: '' }, // ✅ Init object rỗng
  title_vi: 'Tiêu đề bài viết mới', title_en: 'New Article Title',
  content_vi: '<p>Nội dung Tiếng Việt...</p>', content_en: '<p>English Content...</p>',
  author_vi: 'Admin', author_en: 'Admin',
  category_vi: 'Tin tức', category_en: 'News',
};

const ArticleForm = ({ article, setArticle, onSave, onDelete, onCancel, onUpdate }: { article: NewsData; setArticle: React.Dispatch<React.SetStateAction<NewsData | null>>; onSave: (articleToSave: NewsData) => void; onDelete: (slug: string) => void; onCancel: () => void; onUpdate: (updatedArticle: NewsData) => void; }) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => { setArticle(prev => prev ? { ...prev, [e.target.name]: e.target.value } : null); };
  
  // =============================================================================
  // 2. LOGIC UPLOAD ẢNH MỚI (Giống bên Product)
  // =============================================================================
  const handleThumbnailUpload = async (e: ChangeEvent<HTMLInputElement>) => { 
    let file = e.target.files?.[0]; 
    if (!file) return; 

    // Nén ảnh
    try {
      const options = { maxSizeMB: 1, maxWidthOrHeight: 1200, useWebWorker: true, fileType: "image/webp" };
      file = await imageCompression(file, options);
    } catch (err) { console.warn("Lỗi nén ảnh:", err); }

    const formData = new FormData(); 
    formData.append('file', file); // ✅ Key là 'file'

    setIsUploading(true); 
    try { 
      const token = getAuthToken(); 
      if (!token) { window.alert('Vui lòng đăng nhập lại!'); return; } 
      const config = { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }, }; 
      
      // ✅ Gọi endpoint mới
      const { data } = await api.post(`/upload/image?scope=news`, formData, config); 
      
      const { url, public_id } = data;

      // ✅ Cập nhật state & giữ ID cũ để xoá an toàn
      setArticle(prev => {
        if (!prev) return null;
        return {
           ...prev,
           thumbnail: { url, public_id },
           // Nếu đã có oldThumbnailId thì giữ nguyên, chưa có thì lấy ID hiện tại làm old
           oldThumbnailId: prev.oldThumbnailId ? prev.oldThumbnailId : prev.thumbnail?.public_id
        };
      });

    } catch (error) { 
      console.error(error); window.alert('Tải ảnh lên thất bại.'); 
    } finally { 
      setIsUploading(false); 
    } 
  };

  const handleSubmit = (e: FormEvent) => { e.preventDefault(); const cleanArticle = { ...article, content_vi: DOMPurify.sanitize(article.content_vi), content_en: DOMPurify.sanitize(article.content_en), }; onSave(cleanArticle); };
  
  const handleDeleteClick = () => { if (window.confirm(`Bạn có chắc chắn muốn xóa bài viết "${article.title_vi}"?`)) { onDelete(article.slug); } };
  
  const handleToggleVisibility = async () => { 
    try { 
      const token = getAuthToken(); 
      const apiConfig = { headers: { Authorization: `Bearer ${token}` } }; 
      const { data: updated } = await api.patch(`/news/${article.slug}/visibility`, { isVisible: !article.isVisible }, apiConfig); 
      setArticle(updated); 
      onUpdate(updated); 
    } catch (err) { 
      window.alert('Lỗi cập nhật trạng thái'); 
    } 
  };

  const modules = useMemo(() => ({ toolbar: [[{ header: [1, 2, 3, false] }], ['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'image'], ['clean']], }), []);
  const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'image'];
  const handleContentViChange = (value: string) => { setArticle(prev => prev ? { ...prev, content_vi: value } : null); };
  const handleContentEnChange = (value: string) => { setArticle(prev => prev ? { ...prev, content_en: value } : null); };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-md">
      <div className="p-3 rounded-md flex items-center justify-between bg-yellow-100 border border-yellow-300"><span className="font-bold text-yellow-800">Trạng thái: {article.isVisible ? '🟢 Đang hiển thị' : '🔴 Đang ẩn'}</span><button type="button" onClick={handleToggleVisibility} className={`px-4 py-2 text-white font-semibold rounded-md ${article.isVisible ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}>{article.isVisible ? 'Tắt Hiển Thị' : 'Bật Hiển Thị'}</button></div>
      <div><label className="block text-sm font-bold text-gray-700 mb-1">Slug (URL)</label><input type="text" name="slug" value={article.slug} onChange={handleChange} required className="w-full p-2 border rounded bg-gray-100" /></div>
      
      {/* 3. HIỂN THỊ ẢNH (Update URL access) */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-1">Ảnh đại diện</label>
        <input type="file" onChange={handleThumbnailUpload} className="w-full text-sm mb-2" />
        {isUploading && <p className="text-blue-500 text-sm">Đang nén & tải lên...</p>}
        {article.thumbnail?.url && (
            <div className="w-full h-48 mt-2 rounded overflow-hidden border bg-gray-50">
                <img src={createAbsoluteUrl(article.thumbnail.url)} alt="Xem trước" className="w-full h-full object-contain"/>
            </div>
        )}
        <p className="text-xs text-gray-500 mt-1">Khuyến nghị: 1200×630px</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border rounded-md">
        <div>
          <h4 className="font-semibold text-blue-700 mb-2">Nội dung Tiếng Việt</h4>
          <label className="block text-sm font-bold text-gray-700 mb-1">Tiêu đề</label><input type="text" name="title_vi" value={article.title_vi} onChange={handleChange} required className="w-full p-2 border rounded mb-2"/>
          <label className="block text-sm font-bold text-gray-700 mb-1">Tác giả</label><input type="text" name="author_vi" value={article.author_vi} onChange={handleChange} required className="w-full p-2 border rounded mb-2"/>
          <label className="block text-sm font-bold text-gray-700 mb-1">Chuyên mục</label><input type="text" name="category_vi" value={article.category_vi} onChange={handleChange} required className="w-full p-2 border rounded mb-2"/>
          <label className="block text-sm font-bold text-gray-700 mb-1">Nội dung</label><ReactQuill theme="snow" value={article.content_vi} onChange={handleContentViChange} modules={modules} formats={formats}/>
        </div>
        <div>
          <h4 className="font-semibold text-green-700 mb-2">English Content</h4>
          <label className="block text-sm font-bold text-gray-700 mb-1">Title</label><input type="text" name="title_en" value={article.title_en} onChange={handleChange} required className="w-full p-2 border rounded mb-2"/>
          <label className="block text-sm font-bold text-gray-700 mb-1">Author</label><input type="text" name="author_en" value={article.author_en} onChange={handleChange} required className="w-full p-2 border rounded mb-2"/>
          <label className="block text-sm font-bold text-gray-700 mb-1">Category</label><input type="text" name="category_en" value={article.category_en} onChange={handleChange} required className="w-full p-2 border rounded mb-2"/>
          <label className="block text-sm font-bold text-gray-700 mb-1">Content</label><ReactQuill theme="snow" value={article.content_en} onChange={handleContentEnChange} modules={modules} formats={formats}/>
        </div>
      </div>
      <div className="flex justify-between items-center pt-4 border-t"><button type="button" onClick={handleDeleteClick} className="px-4 py-2 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700">Xóa bỏ</button><div className="space-x-4"><button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300">Hủy</button><button type="submit" className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700">Lưu Bài Viết</button></div></div>
    </form>
  );
};

const QLTinTuc = () => {
  const [articles, setArticles] = useState<NewsData[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<NewsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchArticles = async () => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      if (!token) { window.alert('Vui lòng đăng nhập lại!'); return; }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await api.get(`/news?admin=true`, config);
      setArticles(data.data);
    } catch (error) {
      console.error('Lỗi khi tải danh sách bài viết:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleAddNew = () => { const tempSlug = `bai-viet-moi-${Date.now()}`; setSelectedArticle({ ...blankArticle, slug: tempSlug }); };
  
  // =============================================================================
  // 4. XỬ LÝ SAVE (Gửi oldThumbnailId)
  // =============================================================================
  const handleSaveArticle = async (articleToSave: NewsData) => {
    const token = getAuthToken();
    if (!token) { window.alert('Vui lòng đăng nhập lại!'); return; }
    const apiConfig = { headers: { Authorization: `Bearer ${token}` } };
    try {
      if (!articleToSave._id) {
        await api.post(`/news`, articleToSave, apiConfig);
        alert(`Đã tạo thành công bài viết!`);
      } else {
        // Backend sẽ tự đọc oldThumbnailId từ body và xoá ảnh cũ nếu có
        await api.put(`/news/${articleToSave.slug}`, articleToSave, apiConfig);
        alert(`Đã cập nhật thành công bài viết!`);
      }
      setSelectedArticle(null);
      fetchArticles();
    } catch (error: any) { console.error(error); window.alert(error.response?.data?.message || 'Thao tác thất bại!'); }
  };
  
  const handleDeleteArticle = async (slug: string) => {
    const token = getAuthToken();
    if (!token) { window.alert('Vui lòng đăng nhập lại!'); return; }
    try {
      const apiConfig = { headers: { Authorization: `Bearer ${token}` } };
      // Delete news -> BE tự động xoá thumbnail kèm theo
      await api.delete(`/news/${slug}`, apiConfig);
      alert('Đã xóa bài viết thành công!');
      setSelectedArticle(null);
      fetchArticles();
    } catch (error: any) { console.error(error); window.alert(error.response?.data?.message || 'Xóa thất bại!'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Quản lý Tin tức</h1>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-4 lg:col-span-4 bg-white p-4 rounded-lg shadow-md self-start">
          <button onClick={handleAddNew} className="w-full mb-4 px-4 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700">+ Thêm Bài viết mới</button>
          <h2 className="text-xl font-semibold mb-2 border-b pb-2">Danh sách Bài viết</h2>
          {isLoading ? (<p>Đang tải...</p>) : (<ul className="space-y-2 max-h-[70vh] overflow-y-auto">{articles.map(article => (<li key={article._id}><button onClick={() => setSelectedArticle(JSON.parse(JSON.stringify(article)))} className={`w-full text-left p-3 rounded-md text-sm ${selectedArticle?._id === article._id ? 'bg-blue-600 text-white font-bold' : 'bg-gray-50 hover:bg-blue-100'}`}><p className="font-semibold truncate">{article.title_vi}</p><p className={`text-xs ${selectedArticle?._id === article._id ? 'text-blue-200' : 'text-gray-500'}`}>{new Date(article.createdAt || Date.now()).toLocaleDateString('vi-VN')}</p></button></li>))}</ul>)}
        </div>
        <div className="md:col-span-8 lg:col-span-8">
          {selectedArticle ? (<ArticleForm article={selectedArticle} setArticle={setSelectedArticle} onSave={handleSaveArticle} onDelete={handleDeleteArticle} onCancel={() => setSelectedArticle(null)} onUpdate={(updatedArticle) => setArticles(prev => prev.map(a => (a._id === updatedArticle._id ? updatedArticle : a)))}/>) 
          : (<div className="flex items-center justify-center h-full bg-white p-6 rounded-lg shadow-md min-h-[400px]"><p className="text-xl text-gray-500 text-center">Chọn một bài viết để chỉnh sửa, <br /> hoặc thêm bài viết mới.</p></div>)}
        </div>
      </div>
    </div>
  );
};

export default QLTinTuc;